import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';

interface User {
  id: string;
  email: string;
  displayName: string;
  handle: string;
  role: 'user' | 'admin';
  status: 'active' | 'suspended' | 'banned';
  createdAt: string;
  postCount: number;
  reportCount: number;
}

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'suspended' | 'banned'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const res = await api.admin.getUsers({ 
          search: search || undefined,
          status: filter === 'all' ? undefined : filter 
        });
        setUsers(res);
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setLoading(false);
      }
    };
    
    const timer = setTimeout(loadUsers, 300);
    return () => clearTimeout(timer);
  }, [search, filter]);
  
  const handleSuspend = async (userId: string, duration: number | string) => {
    try {
      await api.admin.suspendUser(userId, { duration: Number(duration) });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, status: 'suspended' as const } : u))
      );
      setSelectedUser(null);
    } catch (err) {
      console.error('Failed to suspend user:', err);
    }
  };
  
  const handleBan = async (userId: string, reason: string) => {
    if (!confirm('Are you sure you want to permanently ban this user?')) return;
    
    try {
      await api.admin.banUser(userId, { reason });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, status: 'banned' as const } : u))
      );
      setSelectedUser(null);
    } catch (err) {
      console.error('Failed to ban user:', err);
    }
  };
  
  const handleUnban = async (userId: string) => {
    try {
      await api.admin.unbanUser(userId);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, status: 'active' as const } : u))
      );
      setSelectedUser(null);
    } catch (err) {
      console.error('Failed to unban user:', err);
    }
  };
  
  const handleWarn = async (userId: string, message: string) => {
    try {
      await api.admin.warnUser(userId, { message });
      alert('Warning sent successfully');
    } catch (err) {
      console.error('Failed to warn user:', err);
    }
  };
  
  const getStatusBadge = (status: User['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'banned':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    }
  };
  
  return (
    <div className="p-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Users</h1>
          <p className="text-slate-500 dark:text-slate-400">{users.length} users</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="input w-full sm:w-64"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="input"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="banned">Banned</option>
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Posts</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Joined</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                  <td className="px-4 py-3"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32 animate-pulse" /></td>
                  <td className="px-4 py-3"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 animate-pulse" /></td>
                  <td className="px-4 py-3"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-12 animate-pulse" /></td>
                  <td className="px-4 py-3"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 animate-pulse" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Users table */}
          <div className="lg:col-span-2 card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">User</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Posts</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      onClick={() => setSelectedUser(user)}
                      className={`border-t border-slate-100 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 ${
                        selectedUser?.id === user.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="avatar avatar-sm">
                            {(user.displayName || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {user.displayName}
                              {user.role === 'admin' && (
                                <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 text-xs rounded">
                                  Admin
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-slate-500">@{user.handle}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(user.status)}`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {user.postCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                        {formatDateTime(new Date(user.createdAt))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* User detail */}
          <div className="lg:col-span-1">
            {selectedUser ? (
              <div className="card p-6 sticky top-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="avatar avatar-lg">
                    {(selectedUser.displayName || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">
                      {selectedUser.displayName}
                    </h3>
                    <p className="text-sm text-slate-500">@{selectedUser.handle}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <p className="text-lg font-semibold text-slate-900 dark:text-white">{selectedUser.postCount}</p>
                      <p className="text-xs text-slate-500">Posts</p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <p className="text-lg font-semibold text-slate-900 dark:text-white">{selectedUser.reportCount}</p>
                      <p className="text-xs text-slate-500">Reports</p>
                    </div>
                  </div>
                  
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Email</span>
                      <span className="text-slate-900 dark:text-white">{selectedUser.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Role</span>
                      <span className="text-slate-900 dark:text-white capitalize">{selectedUser.role}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Status</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(selectedUser.status)}`}>
                        {selectedUser.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Role</span>
                      <span className="text-slate-900 dark:text-white capitalize">
                        {selectedUser.role}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-3">
                    <button
                      onClick={() => {
                        const message = prompt('Enter warning message:');
                        if (message) handleWarn(selectedUser.id, message);
                      }}
                      className="btn btn-secondary w-full"
                    >
                      Send Warning
                    </button>
                    
                    {selectedUser.status === 'active' && (
                      <>
                        <button
                          onClick={() => {
                            const hours = prompt('Suspend for how many hours?', '24');
                            if (hours) handleSuspend(selectedUser.id, parseInt(hours));
                          }}
                          className="btn bg-yellow-100 hover:bg-yellow-200 text-yellow-700 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 dark:text-yellow-400 w-full"
                        >
                          Suspend User
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt('Enter ban reason:');
                            if (reason) handleBan(selectedUser.id, reason);
                          }}
                          className="btn bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400 w-full"
                        >
                          Ban User
                        </button>
                      </>
                    )}
                    
                    {(selectedUser.status === 'suspended' || selectedUser.status === 'banned') && (
                      <button
                        onClick={() => handleUnban(selectedUser.id)}
                        className="btn bg-green-100 hover:bg-green-200 text-green-700 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-400 w-full"
                      >
                        {selectedUser.status === 'suspended' ? 'Remove Suspension' : 'Unban User'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="card p-12 text-center">
                <p className="text-slate-500 dark:text-slate-400">
                  Select a user to view details
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
